// Global Instructions Rule Applied!
import { AccountRecordFields } from '../../../state_manager/data_models/account_record.ts'
import type { FormInstance } from 'antd'

export interface BusinessRuleResult {
  formData: Record<string, any>
  formUpdates?: Record<string, any>
  helperFieldUpdates?: Record<string, any>
}

/**
 * Handle city change - auto-populate country when city is selected
 */
function handleCityChange(field: string, value: any, currentFormData: Record<string, any>): BusinessRuleResult | null {
  // Handle business city
  if (
    field === AccountRecordFields.accounts__business_city_label ||
    field === AccountRecordFields.accounts__business_city_code
  ) {
    const newData: Record<string, any> = { ...currentFormData, [field]: value }
    const formUpdates: Record<string, any> = {}

    const countryCode = value?.data?.countryCode || value?.countryCode
    const cityLabel = value?.name || value?.label || ''
    const state = value?.data?.state ?? null
    const cityCode = typeof value === 'object' ? value?.value || value?.code : value

    if (cityCode !== undefined && cityCode !== null && cityCode !== '') {
      if (typeof value === 'object' && value !== null) {
        newData[AccountRecordFields.accounts__business_city_label] = value
        formUpdates[AccountRecordFields.accounts__business_city_label] = value
      } else {
        const cityObject = {
          value: cityCode,
          name: cityLabel || `City ${cityCode}`,
          label: cityLabel || `City ${cityCode}`,
          data: { countryCode }
        }
        newData[AccountRecordFields.accounts__business_city_label] = cityObject
        formUpdates[AccountRecordFields.accounts__business_city_label] = cityObject
      }
    }

    newData[AccountRecordFields.accounts__business_stateprovince] = state || null
    formUpdates[AccountRecordFields.accounts__business_stateprovince] = state || null

    if (countryCode) {
      newData[AccountRecordFields.accounts__business_country_code] = countryCode
      formUpdates[AccountRecordFields.accounts__business_country_code] = countryCode
      newData[AccountRecordFields.accounts__business_country_label] = countryCode
      formUpdates[AccountRecordFields.accounts__business_country_label] = countryCode
    }

    return {
      formData: newData,
      formUpdates: Object.keys(formUpdates).length > 0 ? formUpdates : undefined
    }
  }

  // Handle office city
  if (
    field === AccountRecordFields.accounts__office_city_code__label ||
    field === AccountRecordFields.accounts__office_city_code
  ) {
    const newData: Record<string, any> = { ...currentFormData, [field]: value }
    const formUpdates: Record<string, any> = {}

    const countryCode = value?.data?.countryCode || value?.countryCode
    const cityLabel = value?.name || value?.label || ''
    const state = value?.data?.state ?? null
    const cityCode = typeof value === 'object' ? value?.value || value?.code : value

    if (cityCode !== undefined && cityCode !== null && cityCode !== '') {
      if (typeof value === 'object' && value !== null) {
        newData[AccountRecordFields.accounts__office_city_code__label] = value
        formUpdates[AccountRecordFields.accounts__office_city_code__label] = value
      } else {
        const cityObject = {
          value: cityCode,
          name: cityLabel || `City ${cityCode}`,
          label: cityLabel || `City ${cityCode}`,
          data: { countryCode }
        }
        newData[AccountRecordFields.accounts__office_city_code__label] = cityObject
        formUpdates[AccountRecordFields.accounts__office_city_code__label] = cityObject
      }
    }

    newData[AccountRecordFields.accounts__office_stateprovince] = state || null
    formUpdates[AccountRecordFields.accounts__office_stateprovince] = state || null

    if (countryCode) {
      newData[AccountRecordFields.accounts__office_country_code] = countryCode
      formUpdates[AccountRecordFields.accounts__office_country_code] = countryCode
      newData[AccountRecordFields.accounts__office_country_code__label] = countryCode
      formUpdates[AccountRecordFields.accounts__office_country_code__label] = countryCode
    }

    return {
      formData: newData,
      formUpdates: Object.keys(formUpdates).length > 0 ? formUpdates : undefined
    }
  }

  // Handle founded city
  if (
    field === AccountRecordFields.accounts__founded_in_city_label ||
    field === AccountRecordFields.accounts__founded_in_city_code
  ) {
    const newData: Record<string, any> = { ...currentFormData, [field]: value }
    const formUpdates: Record<string, any> = {}

    // Extract countryCode and state from cityObject.data (matching inputManagerMapper pattern)
    const countryCode = value?.data?.countryCode || value?.countryCode
    const state = value?.data?.state ?? null
    const cityLabel = value?.name || value?.label || ''
    // Extract city code - handle both object format ({value: code}) and direct code format
    const cityCode = typeof value === 'object' ? value?.value || value?.code : value

    // Always persist the full city object in both formData and formUpdates
    // InputManager reads from formData (via fields prop), so we need the full object there
    // CitySelect component expects object format {value: code, name: label, data: {...}}
    if (cityCode !== undefined && cityCode !== null && cityCode !== '') {
      // Preserve the full city object for formData (InputManager reads from here)
      if (typeof value === 'object' && value !== null) {
        newData[AccountRecordFields.accounts__founded_in_city_label] = value
        formUpdates[AccountRecordFields.accounts__founded_in_city_label] = value
      } else {
        // If value is not an object, create one from the code
        const cityObject = {
          value: cityCode,
          name: cityLabel || `City ${cityCode}`,
          label: cityLabel || `City ${cityCode}`,
          data: { countryCode }
        }
        newData[AccountRecordFields.accounts__founded_in_city_label] = cityObject
        formUpdates[AccountRecordFields.accounts__founded_in_city_label] = cityObject
      }
    }

    // Auto-populate state/province when city is selected (cities API provides state)
    if (state !== null) {
      newData[AccountRecordFields.accounts__founded_in_stateprovince] = state
      formUpdates[AccountRecordFields.accounts__founded_in_stateprovince] = state
    } else if (cityCode === undefined || cityCode === null || cityCode === '') {
      newData[AccountRecordFields.accounts__founded_in_stateprovince] = null
      formUpdates[AccountRecordFields.accounts__founded_in_stateprovince] = null
    }

    // Auto-populate country when city is selected
    // Set country code - CountrySelect will resolve the name from the code
    // We need to ensure the value changes so CountrySelect re-initializes
    if (countryCode) {
      // Force update by setting to a new value (even if same code) to trigger re-render
      newData[AccountRecordFields.accounts__founded_in_country_code] = countryCode
      formUpdates[AccountRecordFields.accounts__founded_in_country_code] = countryCode
    }

    return {
      formData: newData,
      formUpdates: Object.keys(formUpdates).length > 0 ? formUpdates : undefined
    }
  }

  return null
}

/**
 * Handle country change - clear city when country changes
 */
function handleCountryChange(
  field: string,
  value: any,
  currentFormData: Record<string, any>
): BusinessRuleResult | null {
  // Handle business country
  if (
    field === AccountRecordFields.accounts__business_country_code ||
    field === AccountRecordFields.accounts__business_country_label
  ) {
    const newData: Record<string, any> = { ...currentFormData, [field]: value }
    const formUpdates: Record<string, any> = {}

    // Extract country code and label from country object
    const countryCode = value?.value || value
    const countryLabel = value?.label || value

    // Clear city and state when country changes
    newData[AccountRecordFields.accounts__business_city_code] = null
    newData[AccountRecordFields.accounts__business_city_label] = null
    newData[AccountRecordFields.accounts__business_stateprovince] = null
    formUpdates[AccountRecordFields.accounts__business_city_code] = null
    formUpdates[AccountRecordFields.accounts__business_city_label] = null
    formUpdates[AccountRecordFields.accounts__business_stateprovince] = null

    // Set or clear country code and label (must clear both so City search filter resets)
    if (countryCode) {
      newData[AccountRecordFields.accounts__business_country_code] = countryCode
      formUpdates[AccountRecordFields.accounts__business_country_code] = countryCode

      // Set business_country_label (companies form uses this as the field key)
      // Use countryLabel if available, otherwise use countryCode (CountrySelect will look it up)
      const finalCountryLabel = countryLabel && countryLabel !== countryCode ? countryLabel : countryCode
      newData[AccountRecordFields.accounts__business_country_label] = finalCountryLabel
      formUpdates[AccountRecordFields.accounts__business_country_label] = finalCountryLabel
    } else {
      newData[AccountRecordFields.accounts__business_country_code] = null
      formUpdates[AccountRecordFields.accounts__business_country_code] = null
      newData[AccountRecordFields.accounts__business_country_label] = null
      formUpdates[AccountRecordFields.accounts__business_country_label] = null
    }

    return {
      formData: newData,
      formUpdates: Object.keys(formUpdates).length > 0 ? formUpdates : undefined
    }
  }

  // Handle office country
  if (
    field === AccountRecordFields.accounts__office_country_code ||
    field === AccountRecordFields.accounts__office_country_code__label
  ) {
    const newData: Record<string, any> = { ...currentFormData, [field]: value }
    const formUpdates: Record<string, any> = {}

    // Extract country code and label from country object
    const countryCode = value?.value || value
    const countryLabel = value?.label || value

    // Clear city and state when country changes
    newData[AccountRecordFields.accounts__office_city_code] = null
    newData[AccountRecordFields.accounts__office_city_code__label] = null
    newData[AccountRecordFields.accounts__office_stateprovince] = null
    formUpdates[AccountRecordFields.accounts__office_city_code] = null
    formUpdates[AccountRecordFields.accounts__office_city_code__label] = null
    formUpdates[AccountRecordFields.accounts__office_stateprovince] = null

    // Set or clear country code and label (must clear both so City search filter resets)
    if (countryCode) {
      newData[AccountRecordFields.accounts__office_country_code] = countryCode
      formUpdates[AccountRecordFields.accounts__office_country_code] = countryCode

      // Set office_country_code__label (companies form uses this as the field key)
      // Use countryLabel if available, otherwise use countryCode (CountrySelect will look it up)
      const finalCountryLabel = countryLabel && countryLabel !== countryCode ? countryLabel : countryCode
      newData[AccountRecordFields.accounts__office_country_code__label] = finalCountryLabel
      formUpdates[AccountRecordFields.accounts__office_country_code__label] = finalCountryLabel
    } else {
      newData[AccountRecordFields.accounts__office_country_code] = null
      formUpdates[AccountRecordFields.accounts__office_country_code] = null
      newData[AccountRecordFields.accounts__office_country_code__label] = null
      formUpdates[AccountRecordFields.accounts__office_country_code__label] = null
    }

    return {
      formData: newData,
      formUpdates: Object.keys(formUpdates).length > 0 ? formUpdates : undefined
    }
  }

  // Handle founded country
  if (field === AccountRecordFields.accounts__founded_in_country_code) {
    const newData: Record<string, any> = { ...currentFormData, [field]: value }
    const formUpdates: Record<string, any> = {}

    // Extract country code and label from country object
    const countryCode = value?.value || value
    const countryLabel = value?.label || value

    // Clear city and state when country changes
    newData[AccountRecordFields.accounts__founded_in_city_code] = null
    newData[AccountRecordFields.accounts__founded_in_city_label] = null
    newData[AccountRecordFields.accounts__founded_in_stateprovince] = null
    formUpdates[AccountRecordFields.accounts__founded_in_city_code] = null
    formUpdates[AccountRecordFields.accounts__founded_in_city_label] = null
    formUpdates[AccountRecordFields.accounts__founded_in_stateprovince] = null

    // Set or clear country code (must clear so City search filter resets)
    if (countryCode) {
      newData[AccountRecordFields.accounts__founded_in_country_code] = countryCode
      formUpdates[AccountRecordFields.accounts__founded_in_country_code] = countryCode
    } else {
      newData[AccountRecordFields.accounts__founded_in_country_code] = null
      formUpdates[AccountRecordFields.accounts__founded_in_country_code] = null
    }

    return {
      formData: newData,
      formUpdates: Object.keys(formUpdates).length > 0 ? formUpdates : undefined
    }
  }

  return null
}

/**
 * Main handler for field changes - applies all business rules
 */
export function handleFieldChange(
  field: string,
  value: any,
  currentFormData: Record<string, any>,
  form: FormInstance
): BusinessRuleResult {
  let newData = { ...currentFormData, [field]: value }

  const cityResult = handleCityChange(field, value, newData)
  if (cityResult) {
    newData = cityResult.formData
    if (cityResult.formUpdates) {
      form.setFieldsValue(cityResult.formUpdates)
    }
    return cityResult
  }

  const countryResult = handleCountryChange(field, value, newData)
  if (countryResult) {
    newData = countryResult.formData
    if (countryResult.formUpdates) {
      form.setFieldsValue(countryResult.formUpdates)
    }
    return countryResult
  }

  // Default: just update the field
  return {
    formData: newData
  }
}
